import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_AVAILABLE_SURVEYS, GET_AVAILABLE_SURVEYS_DIPENDENTE, GET_MY_COMPLETED_SURVEYS, GET_MY_EXPIRED_SURVEYS } from '../services/surveyQueries';
import { getAssociateToken, getSurveyClient, percorsiArea } from '../services/surveyAuth';
import { CalendarX, Clock, Eye, Lock, UserRound } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import { eDipendente } from '../utils/sezioniDipendente';

interface Survey {
  id: string;
  title: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  // Sondaggio territoriale senza titolo per l'associato: resta in elenco, non si apre
  avvisoTerritorio?: string | null;
  // Solo per il dipendente dello studio: true se risponde solo il titolare
  rispondeSoloTitolare?: boolean | null;
  steps: { stepId: string; title: string }[];
}

interface CompletedSurvey {
  surveyId: string;
  title: string;
  description: string | null;
  completedAt: string;
  stepCount: number;
}

// Sondaggio chiuso o scaduto senza una risposta inviata: solo da consultare
interface ExpiredSurvey {
  surveyId: string;
  title: string;
  description: string | null;
  closedAt: string;
  stepCount: number;
}

const SurveyListPage: React.FC = () => {
  const navigate = useNavigate();

  const getPartitaIVA = () => {
    const associateData = localStorage.getItem('associate_data');
    if (associateData) {
      try {
        const parsed = JSON.parse(associateData);
        return parsed.partitaIVA || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  };

  const partitaIVA = getPartitaIVA();

  // Dipendente dello studio: la stessa lista con `rispondeSoloTitolare`, con il suo Bearer (il
  // server sa chi è) e il client dei sondaggi. Il titolare resta sulla query di sempre.
  const dipendente = (() => {
    try { return eDipendente(JSON.parse(localStorage.getItem('associate_data') || 'null')); } catch { return false; }
  })();
  const tokenDipendente = dipendente ? getAssociateToken() : null;

  const { data: datiTitolare, loading: loadingTitolare } = useQuery(GET_AVAILABLE_SURVEYS, {
    variables: { partitaIVA },
    skip: !partitaIVA || dipendente,
    fetchPolicy: 'cache-and-network'
  });

  const { data: datiDipendente, loading: loadingDipendente } = useQuery(GET_AVAILABLE_SURVEYS_DIPENDENTE, {
    client: dipendente ? getSurveyClient() : undefined,
    context: tokenDipendente ? { headers: { Authorization: `Bearer ${tokenDipendente}` } } : undefined,
    variables: { partitaIVA },
    skip: !partitaIVA || !dipendente,
    fetchPolicy: 'network-only'
  });

  const data = dipendente ? datiDipendente : datiTitolare;
  const loading = dipendente ? loadingDipendente : loadingTitolare;

  const { data: completedData, loading: completedLoading } = useQuery(GET_MY_COMPLETED_SURVEYS, {
    variables: { partitaIVA },
    skip: !partitaIVA,
    fetchPolicy: 'cache-and-network'
  });

  const { data: expiredData, loading: expiredLoading } = useQuery(GET_MY_EXPIRED_SURVEYS, {
    variables: { partitaIVA },
    skip: !partitaIVA,
    fetchPolicy: 'cache-and-network'
  });

  const surveys: Survey[] = data?.availableSurveys || [];
  const completedSurveys: CompletedSurvey[] = completedData?.myCompletedSurveys || [];
  const expiredSurveys: ExpiredSurvey[] = expiredData?.myExpiredSurveys || [];

  const isLoading = loading || completedLoading || expiredLoading;

  if (isLoading && surveys.length === 0 && completedSurveys.length === 0 && expiredSurveys.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb items={[
            { label: 'Area Riservata', href: '/area-riservata' },
            { label: 'Sondaggi', current: true }
          ]} />
          <div className="mt-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">I tuoi Sondaggi</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Partecipa ai sondaggi attivi per aiutarci a migliorare i nostri servizi
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">

        {/* ── Sondaggi disponibili ── */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
            Disponibili
            {surveys.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                {surveys.length}
              </span>
            )}
          </h2>

          {surveys.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <p className="mt-3 text-gray-500 dark:text-gray-400 text-sm">
                Al momento non ci sono sondaggi attivi per te.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {surveys.map((survey) => (
                <div
                  key={survey.id}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 ${survey.avvisoTerritorio ? '' : 'hover:shadow-md transition-shadow'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {survey.title}
                      </h3>
                      {survey.description && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 line-clamp-2">
                          {survey.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {survey.steps.length} {survey.steps.length === 1 ? 'step' : 'step'}
                        </span>
                        {survey.endDate && (
                          <span className="flex items-center gap-1 text-amber-500">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Scade il {new Date(survey.endDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                      {survey.avvisoTerritorio && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                          <Lock className="w-3.5 h-3.5 mt-px flex-shrink-0" aria-hidden="true" />
                          <span>{survey.avvisoTerritorio}</span>
                        </p>
                      )}
                    </div>
                    {survey.avvisoTerritorio ? (
                      <span
                        aria-disabled="true"
                        className="flex-shrink-0 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed select-none"
                      >
                        Non disponibile
                      </span>
                    ) : dipendente && survey.rispondeSoloTitolare ? (
                      <span
                        aria-disabled="true"
                        className="flex-shrink-0 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg select-none inline-flex items-center gap-1.5"
                      >
                        <UserRound className="w-4 h-4" aria-hidden="true" />
                        Risponde il titolare
                      </span>
                    ) : (
                      <button
                        onClick={() => navigate(`/sondaggi/${survey.id}`)}
                        className="flex-shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors"
                      >
                        Partecipa
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Sondaggi completati ── */}
        {completedSurveys.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
              Completati
              <span className="ml-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                {completedSurveys.length}
              </span>
            </h2>

            <div className="space-y-3">
              {completedSurveys.map((survey) => (
                <div
                  key={survey.surveyId}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 border-green-400"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mt-0.5">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {survey.title}
                      </h3>
                      {survey.description && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5 line-clamp-1">
                          {survey.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {survey.stepCount} {survey.stepCount === 1 ? 'step' : 'step'}
                        </span>
                        <span className="flex items-center gap-1 text-green-500">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Completato il {new Date(survey.completedAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                      <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded-full">
                        Completato
                      </span>
                      <Link
                        to={percorsiArea('riservata').risposte(survey.surveyId)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Eye className="w-4 h-4" aria-hidden="true" />
                        Vedi le risposte
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Sondaggi scaduti senza risposta (sola lettura, nessuna azione) ── */}
        {expiredSurveys.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>
              Scaduti senza risposta
              <span className="ml-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium">
                {expiredSurveys.length}
              </span>
            </h2>

            <div className="space-y-3">
              {expiredSurveys.map((survey) => (
                <div
                  key={survey.surveyId}
                  data-testid={`scaduto-${survey.surveyId}`}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 border-gray-300 dark:border-gray-600"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mt-0.5">
                      <CalendarX className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {survey.title}
                      </h3>
                      {survey.description && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5 line-clamp-1">
                          {survey.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {survey.stepCount} step
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                          Chiuso il {new Date(survey.closedAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })} · nessuna risposta inviata
                        </span>
                      </div>
                    </div>
                    <span className="flex-shrink-0 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full">
                      Chiuso
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default SurveyListPage;
